/**
 * frontend/src/components/console/NoVncViewer.jsx
 * -------------------------------------------------
 * Composant VNC via noVNC (RFB over WebSocket).
 *
 * CORRECTIFS :
 *  - "WebSocket closed before connection established" (StrictMode React dev) :
 *    Utilisation d'un sessionId pour ignorer les instances RFB orphelines
 *    créées lors du premier montage StrictMode avant le cleanup.
 *  - disconnect() n'appelle rfb.disconnect() que si le WS noVNC est OPEN
 *    (readyState check via rfb._sock?._websocket?.readyState)
 *  - connect() vérifie le sessionId après chaque await pour abandonner
 *    silencieusement si le composant a été démonté entretemps.
 */

import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from "react";
import {
  Alert, Box, Button, Chip, IconButton, Stack, Tooltip, Typography,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { buildVncWsUrl, buildTokenSubprotocols, getToken } from "../../utils/consoleUrl";

const NOVNC_URL = "https://cdn.jsdelivr.net/npm/@novnc/novnc@1.4.0/core/rfb.js";
const MAX_RECONNECT = 3;
const RECONNECT_DELAY_MS = 2500;
const STATUS_COLOR = {
  idle: "default", loading: "warning", connecting: "warning",
  connected: "success", closed: "default", error: "error",
};

// Import dynamique singleton — évite un double chargement du CDN
let _rfbPromise = null;
async function getRFB() {
  if (_rfbPromise) return _rfbPromise;
  _rfbPromise = import(/* webpackIgnore: true */ NOVNC_URL)
    .then((mod) => mod.default || mod.RFB)
    .catch((e) => { _rfbPromise = null; throw new Error("Chargement noVNC echoue : " + e.message); });
  return _rfbPromise;
}

// Tente de déconnecter proprement un RFB en vérifiant l'état du WS interne
function safeDisconnectRfb(rfb) {
  if (!rfb) return;
  try {
    // noVNC expose _sock._websocket — si le WS est encore CONNECTING (0), on l'abandonne
    const ws = rfb._sock?._websocket;
    if (ws && ws.readyState === WebSocket.CONNECTING) {
      // On ne peut pas .close() proprement — on détache juste les listeners
      // pour que noVNC n'essaie pas de logger l'erreur
      try { rfb._sock._websocket.onclose = null; } catch (_) {}
      try { rfb._sock._websocket.onerror = null; } catch (_) {}
      return; // laisser le GC fermer la connexion
    }
    rfb.disconnect();
  } catch (_) {}
}

export default function NoVncViewer({ host, port, label, onStatusChange }) {
  const [status,     setStatus]     = useState("idle");
  const [errorMsg,   setErrorMsg]   = useState("");
  const [fullscreen, setFullscreen] = useState(false);

  const screenRef      = useRef(null);
  const rfbRef         = useRef(null);
  const mountedRef     = useRef(false);
  const sessionRef     = useRef(0);         // incrémenté à chaque connect() pour invalider les instances orphelines
  const reconnectCount = useRef(0);
  const reconnectTimer = useRef(null);

  const upd = useCallback((s, err = "") => {
    if (!mountedRef.current) return;
    setStatus(s); setErrorMsg(err); onStatusChange?.(s, err);
  }, [onStatusChange]);

  const disconnect = useCallback((resetCount = true) => {
    clearTimeout(reconnectTimer.current);
    reconnectTimer.current = null;
    sessionRef.current += 1; // invalide toute connect() en cours
    if (rfbRef.current) {
      safeDisconnectRfb(rfbRef.current);
      rfbRef.current = null;
    }
    if (resetCount) reconnectCount.current = 0;
  }, []);

  const connect = useCallback(async () => {
    if (!mountedRef.current) return;

    const token = getToken();
    if (!host || !port || !token) { upd("error", "host/port/token manquant"); return; }
    const el = screenRef.current;
    if (!el) { upd("error", "Conteneur VNC non disponible"); return; }

    // Incrémenter la session AVANT l'await pour que les montages orphelins StrictMode soient détectés
    const mySession = sessionRef.current + 1;
    sessionRef.current = mySession;

    safeDisconnectRfb(rfbRef.current);
    rfbRef.current = null;
    upd("loading");

    let RFB;
    try { RFB = await getRFB(); } catch (e) { if (mountedRef.current) upd("error", e.message); return; }

    // Vérifier que ce connect() est encore valide après l'await asynchrone
    if (!mountedRef.current || sessionRef.current !== mySession) return;
    if (!RFB) return;

    // SECWS03 : le token ne fait plus partie de l'URL (voir buildVncWsUrl()) ;
    // il est transmis via le sous-protocole WS "token.<jwt>", en plus de
    // "binary" -- noVNC ne lit "binary" que pour decider arraybuffer/texte
    // cote client et le fixe de toute facon de maniere inconditionnelle,
    // il n'exige donc pas que le serveur confirme precisement "binary".
    const wsUrl = buildVncWsUrl({ host, port: Number(port) });
    const wsProtocols = ["binary", ...buildTokenSubprotocols(token)];
    upd("connecting");

    try {
      const rfb = new RFB(el, wsUrl, { shared: true, wsProtocols, credentials: {} });
      rfb.viewOnly = false; rfb.scaleViewport = true; rfb.resizeSession = true;
      rfb.qualityLevel = 6; rfb.compressionLevel = 6;

      rfb.addEventListener("connect", () => {
        if (!mountedRef.current || sessionRef.current !== mySession) return;
        reconnectCount.current = 0;
        upd("connected");
      });

      rfb.addEventListener("disconnect", (e) => {
        if (!mountedRef.current || sessionRef.current !== mySession) return;
        const clean = e?.detail?.clean === true;
        if (!clean && reconnectCount.current < MAX_RECONNECT) {
          reconnectCount.current++;
          upd("connecting", `Reconnexion ${reconnectCount.current}/${MAX_RECONNECT}...`);
          reconnectTimer.current = setTimeout(() => {
            if (mountedRef.current) connect();
          }, RECONNECT_DELAY_MS);
        } else {
          upd(clean ? "closed" : "error", clean ? "" : "Connexion VNC perdue.");
        }
      });

      rfb.addEventListener("securityfailure", (e) => {
        if (!mountedRef.current || sessionRef.current !== mySession) return;
        upd("error", `Echec securite VNC (${e?.detail?.status ?? "?"}).`);
      });

      // Vérifier une dernière fois après création synchrone du RFB
      if (!mountedRef.current || sessionRef.current !== mySession) {
        safeDisconnectRfb(rfb);
        return;
      }

      rfbRef.current = rfb;
    } catch (e) {
      if (mountedRef.current && sessionRef.current === mySession) {
        upd("error", `Init VNC echouee : ${e?.message ?? String(e)}`);
      }
    }
  }, [host, port, upd]);

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      disconnect(true);
    };
  }, [disconnect]);

  useEffect(() => {
    reconnectCount.current = 0;
    if (host && port) connect();
  }, [host, port]);

  const toggleFullscreen = useCallback(() => {
    const w = screenRef.current?.closest("[data-vnc-wrapper]");
    if (!w) return;
    if (!document.fullscreenElement) { w.requestFullscreen?.(); setFullscreen(true); }
    else { document.exitFullscreen?.(); setFullscreen(false); }
  }, []);

  return (
    <Box data-vnc-wrapper>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ opacity: 0.8 }}>VNC — <strong>{label || `${host}:${port}`}</strong></Typography>
          <Chip size="small" label={status} color={STATUS_COLOR[status] ?? "default"} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined"
            onClick={() => { reconnectCount.current = 0; connect(); }}
            disabled={status === "loading" || status === "connecting"}>
            Reconnect
          </Button>
          <Button size="small" variant="outlined" color="error"
            onClick={() => { disconnect(true); upd("closed"); }}
            disabled={status === "idle" || status === "closed"}>
            Disconnect
          </Button>
          <Tooltip title={fullscreen ? "Quitter plein ecran" : "Plein ecran"}>
            <IconButton size="small" onClick={toggleFullscreen}>
              {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {errorMsg && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErrorMsg("")}>{errorMsg}</Alert>}

      <Box ref={screenRef} sx={{
        width: "100%", height: 520, borderRadius: 1, overflow: "hidden",
        border: "1px solid rgba(0,0,0,0.15)", bgcolor: "#0b0f14",
        cursor: status === "connected" ? "default" : "not-allowed",
        "& canvas": { display: "block", width: "100% !important", height: "100% !important" },
      }} />

      {(status === "idle" || status === "closed") && (
        <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 1, opacity: 0.5 }}>
          {status === "idle" ? "Cliquez Reconnect pour demarrer la session VNC." : "Session fermee. Cliquez Reconnect."}
        </Typography>
      )}
    </Box>
  );
}
